'use client';

import { LiveClock } from '@/components/LiveClock';
import { LocationIndicator } from '@/components/LocationIndicator';
import { MonitoringGraph } from '@/components/MonitoringGraph';
import { EyeTracker } from '@/components/EyeTracker';
import { KonamiCode } from '@/components/KonamiCode';
import { ErrorBoundary } from '@/components/ErrorBoundary';

export default function HomePage() {
  return (
    <>
      {/* Skip navigation for accessibility */}
      <a href="#main-content" className="skip-link sr-only focus:not-sr-only">
        Skip to main content
      </a>

      {/* Background Pattern matching v1 */}
      <div className="background-pattern" role="presentation" aria-hidden="true" />

      <main className="container" id="main-content">
        <header>
          <div
            className="observing-icon"
            role="img"
            aria-label="Interactive eyes that follow your cursor"
          >
            <EyeTracker />
          </div>
          <h1 className="logo">Petch Umpriwan</h1>
          <p className="subtitle">🔍 Observing everything, everywhere, all at once ⚡</p>
        </header>

        <section className="status-section" aria-label="System Status">
          <h2 className="sr-only">Current System Status</h2>

          <div className="status-indicators">
            <div className="status-indicator clock-indicator" role="status" aria-label="Current time">
              <div className="status-dot clock-dot" aria-hidden="true"></div>
              <span className="live-clock">
                <ErrorBoundary fallback={<span>Time unavailable</span>}>
                  <LiveClock />
                </ErrorBoundary>
              </span>
            </div>
            <div className="status-indicator location-indicator" role="status" aria-label="Location">
              <div className="status-dot location-dot" aria-hidden="true"></div>
              <span className="live-location">
                <ErrorBoundary fallback={<span>Location unavailable</span>}>
                  <LocationIndicator />
                </ErrorBoundary>
              </span>
            </div>
          </div>

          <div className="status-indicators">
            <div className="status-indicator" role="status" aria-label="Systems status">
              <div className="status-dot" aria-hidden="true"></div>
              <span>🎯 Systems: All green!</span>
            </div>
            <div className="status-indicator" role="status" aria-label="Monitoring status">
              <div className="status-dot" aria-hidden="true"></div>
              <span>📊 Monitoring: Always on</span>
            </div>
          </div>
        </section>

        <section className="intro-section" aria-label="Welcome message">
          <div className="message">
            <p>
              Just your friendly SRE, brewing up some reliability magic! ✨<br />
              Now in observability mode, fine-tuning the perfect monitoring setup... 📈
            </p>
          </div>
        </section>

        <section className="metrics-section" aria-label="Live Metrics">
          <h2 className="sr-only">Live System Metrics</h2>
          <ErrorBoundary fallback={<div className="graph-error">Metrics temporarily unavailable</div>}>
            <MonitoringGraph />
          </ErrorBoundary>
        </section>

        <footer className="contact-info">
          <p>
            💫 Currently: Maintaining uptime @{' '}
            <a
              href="https://agoda.com"
              target="_blank"
              rel="noopener noreferrer"
              className="agoda-link"
              aria-label="Visit Agoda.com (opens in new tab)"
            >
              <span className="agoda-a">a</span>
              <span className="agoda-g">g</span>
              <span className="agoda-o">o</span>
              <span className="agoda-d">d</span>
              <span className="agoda-a2">a</span>
            </a>
            , Tinkering{' '}
            <a
              href="https://grafana.com"
              target="_blank"
              rel="noopener noreferrer"
              className="grafana-link"
              aria-label="Visit Grafana.com (opens in new tab)"
            >
              Grafana
            </a>
            , Building <strong className="text-purple">mitigation</strong> tools, Caffeinating ☕️
          </p>
          <p>
            🎮 Wanna chat about reliability? Find me at{' '}
            <strong>
              <a
                href="https://www.linkedin.com/in/petchumpriwan"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Petch's LinkedIn profile (opens in new tab)"
              >
                LinkedIn
              </a>
            </strong>
          </p>
        </footer>
      </main>

      {/* Konami Code Easter Egg */}
      <KonamiCode />
    </>
  );
}
